import { saveAs } from 'file-saver';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const CPE_LABELS = {
  PENDIENTE: 'Pendiente',
  PROCESANDO: 'Procesando',
  PREPARADO: 'Preparado',
  ENVIADO: 'Enviado a SUNAT',
  ACEPTADO: 'Aceptado',
  RECHAZADO: 'Rechazado',
  ANULADO: 'Anulado'
};

function normalizeText(value) {
  return String(value ?? '').trim();
}

function toDate(value) {
  if (!value) return null;
  const text = normalizeText(value);
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    return new Date(year, month, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateText(value) {
  const date = toDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function resolveEstadoComercial(fechaValidez) {
  if (!fechaValidez) return '';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const venc = toDate(fechaValidez);
  if (!venc) return '';
  venc.setHours(0, 0, 0, 0);
  if (venc < hoy) return 'VENCIDA';
  if ((venc - hoy) / 86400000 <= 3) return 'POR VENCER';
  return 'VIGENTE';
}

function resolveCpeLabel(estadoCpe) {
  const code = normalizeText(estadoCpe).toUpperCase();
  return CPE_LABELS[code] || code || '';
}

function resolveClienteInfo(factura, clientes) {
  const cliente = clientes.find((item) => Number(item.id) === Number(factura.cliente_id));
  if (!cliente) {
    return {
      nombre: factura.cliente_id ? `ID ${factura.cliente_id}` : 'Sin cliente',
      documento: ''
    };
  }

  return {
    nombre: cliente.razon_social || cliente.nombre || cliente.nombres || 'Cliente',
    documento: cliente.numero_documento || ''
  };
}

function moneyValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildRows(facturas, clientes) {
  return facturas.map((factura, index) => {
    const cliente = resolveClienteInfo(factura, clientes);
    const total = moneyValue(factura.total);
    const tipoCambio = moneyValue(factura.tipo_cambio || 1);
    const moneda = normalizeText(factura.moneda || 'PEN') || 'PEN';
    const estadoComercial = resolveEstadoComercial(factura.fecha_validez);
    const estadoCpe = resolveCpeLabel(factura.estado_cpe);
    const estadoAnulacion = normalizeText(factura.ultimo_estado_anulacion);
    const anulada = ['ANULADO', 'RECHAZADO'].includes(factura.estado_cpe)
      || ['ANULADA', 'RECHAZADA'].includes(factura.estado)
      || Number(factura.estado_registro) === 0;

    return {
      item: index + 1,
      id: factura.id,
      documento: `${normalizeText(factura.serie)}-${String(Number(factura.numero || 0)).padStart(8, '0')}`,
      serie: normalizeText(factura.serie),
      numero: Number(factura.numero || 0),
      cliente: cliente.nombre,
      clienteDocumento: cliente.documento,
      fechaEmision: toDate(factura.fecha_emision),
      fechaEmisionText: formatDateText(factura.fecha_emision),
      fechaVencimiento: toDate(factura.fecha_validez),
      fechaVencimientoText: formatDateText(factura.fecha_validez),
      estadoComercial,
      estadoCpe,
      estadoAnulacion,
      estadoGeneral: anulada ? 'ANULADA' : (estadoCpe || estadoComercial || normalizeText(factura.estado) || 'PENDIENTE'),
      moneda,
      tipoCambio,
      subtotal: anulada ? 0 : moneyValue(factura.subtotal),
      igv: anulada ? 0 : moneyValue(factura.total_igv),
      total: anulada ? 0 : total,
      totalPen: anulada ? 0 : (moneda === 'USD' ? total * (tipoCambio || 1) : total),
      observaciones: normalizeText(factura.observaciones)
    };
  });
}

function setThinBorder(cell, color = 'FFD0D7DE') {
  cell.border = {
    top: { style: 'thin', color: { argb: color } },
    left: { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right: { style: 'thin', color: { argb: color } }
  };
}

function styleMergedBlock(worksheet, range, fill, fontColor = 'FFFFFFFF', fontSize = 11) {
  worksheet.mergeCells(range);
  const cell = worksheet.getCell(range.split(':')[0]);
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  cell.font = { bold: true, color: { argb: fontColor }, size: fontSize };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  setThinBorder(cell, fill);
  return cell;
}

function addKpi(worksheet, labelRange, valueRange, label, value, fill) {
  const labelCell = styleMergedBlock(worksheet, labelRange, fill, 'FFFFFFFF', 10);
  labelCell.value = label;

  const valueCell = styleMergedBlock(worksheet, valueRange, 'FFF7FAFC', 'FF123247', 16);
  valueCell.value = value;
  valueCell.font = { bold: true, size: 16, color: { argb: 'FF123247' } };
  setThinBorder(valueCell, 'FFD7E3EC');
}

function colorizeStateCell(cell, value) {
  const state = normalizeText(value).toUpperCase();
  const palette = {
    VIGENTE: { fill: 'FFE8F5E9', font: 'FF1B5E20' },
    'POR VENCER': { fill: 'FFFFF4E5', font: 'FF9A6700' },
    VENCIDA: { fill: 'FFFDECEC', font: 'FFB42318' },
    ACEPTADO: { fill: 'FFE8F5E9', font: 'FF1B5E20' },
    'ENVIADO A SUNAT': { fill: 'FFFFF4E5', font: 'FF9A6700' },
    ENVIADO: { fill: 'FFFFF4E5', font: 'FF9A6700' },
    RECHAZADO: { fill: 'FFFDECEC', font: 'FFB42318' },
    ANULADA: { fill: 'FFF3F4F6', font: 'FF344054' },
    ANULADO: { fill: 'FFF3F4F6', font: 'FF344054' },
    PENDIENTE: { fill: 'FFEFF8FF', font: 'FF175CD3' },
    PROCESANDO: { fill: 'FFEFF8FF', font: 'FF175CD3' }
  }[state];

  if (!palette) return;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: palette.fill } };
  cell.font = { bold: true, color: { argb: palette.font } };
}

function styleHeaderRow(row) {
  row.height = 24;
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 }; 
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    setThinBorder(cell, 'FF123247');
  });
}

function styleBodyRow(row, rowIndex, stateColumnIndexes = []) {
  row.eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: rowIndex % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF' }
    };
    setThinBorder(cell);
  });

  stateColumnIndexes.forEach((columnIndex) => {
    colorizeStateCell(row.getCell(columnIndex), row.getCell(columnIndex).value);
  });
}

function createSummarySheet(workbook, rows, searchText, usuario) {
  const worksheet = workbook.addWorksheet('Resumen Comercial', {
    views: [{ state: 'frozen', ySplit: 8 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });

  worksheet.properties.defaultRowHeight = 21;
  worksheet.columns = [
    { width: 7 },
    { width: 18 },
    { width: 18 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 18 },
    { width: 12 },
    { width: 12 },
    { width: 16 }
  ];

  worksheet.mergeCells('A1:J1');
  worksheet.getCell('A1').value = 'REPORTE PROFESIONAL DE FACTURACION';
  worksheet.getCell('A1').font = { bold: true, size: 18, color: { argb: 'FFFFFFFF' } };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
  worksheet.getRow(1).height = 28;

  worksheet.mergeCells('A2:J2');
  worksheet.getCell('A2').value = `Generado el ${new Date().toLocaleString('es-PE')} ${usuario ? `| Usuario: ${usuario}` : ''}`;
  worksheet.getCell('A2').font = { italic: true, size: 10, color: { argb: 'FF475467' } };
  worksheet.getCell('A2').alignment = { horizontal: 'center' };
  worksheet.getRow(2).height = 20;

  worksheet.mergeCells('A3:J3');
  worksheet.getCell('A3').value = `Filtro aplicado: ${searchText ? searchText : 'Todos los registros visibles'}`;
  worksheet.getCell('A3').font = { size: 10, color: { argb: 'FF344054' } };
  worksheet.getCell('A3').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } };
  worksheet.getCell('A3').alignment = { horizontal: 'left' };

  const totalDocumentos = rows.length;
  const totalImporte = rows.reduce((acc, row) => acc + row.totalPen, 0);
  const vigentes = rows.filter((row) => row.estadoComercial === 'VIGENTE').length;
  const porVencer = rows.filter((row) => row.estadoComercial === 'POR VENCER').length;
  const vencidas = rows.filter((row) => row.estadoComercial === 'VENCIDA').length;
  const aceptadas = rows.filter((row) => row.estadoCpe === 'Aceptado').length;

  addKpi(worksheet, 'A5:B5', 'A6:B7', 'Documentos', totalDocumentos, 'FF175CD3');
  addKpi(worksheet, 'D5:E5', 'D6:E7', 'Importe total (PEN)', totalImporte, 'FF039855');
  worksheet.getCell('D6').numFmt = '#,##0.00';
  addKpi(worksheet, 'G5:H5', 'G6:H7', 'Facturas aceptadas', aceptadas, 'FF7A5AF8');
  addKpi(worksheet, 'I5:J5', 'I6:J7', 'Vencidas', vencidas, 'FFDC2626');
  addKpi(worksheet, 'A8:B8', 'A9:B10', 'Vigentes', vigentes, 'FF027A48');
  addKpi(worksheet, 'D8:E8', 'D9:E10', 'Por vencer', porVencer, 'FFB54708');

  const headerRowIndex = 12;
  const headers = ['Documento', 'Cliente', 'Documento cliente', 'Emision', 'Vence', 'Estado comercial', 'Estado CPE', 'Moneda', 'T/C', 'Total'];
  worksheet.getRow(headerRowIndex).values = headers;
  styleHeaderRow(worksheet.getRow(headerRowIndex));

  rows.forEach((row, index) => {
    const excelRow = worksheet.getRow(headerRowIndex + 1 + index);
    excelRow.values = [
      row.documento,
      row.cliente,
      row.clienteDocumento,
      row.fechaEmision || row.fechaEmisionText,
      row.fechaVencimiento || row.fechaVencimientoText,
      row.estadoComercial,
      row.estadoCpe || row.estadoGeneral,
      row.moneda,
      row.tipoCambio,
      row.total
    ];

    excelRow.getCell(4).numFmt = 'dd/mm/yyyy';
    excelRow.getCell(5).numFmt = 'dd/mm/yyyy';
    excelRow.getCell(9).numFmt = '#,##0.0000';
    excelRow.getCell(10).numFmt = '#,##0.00';
    excelRow.height = 20;
    styleBodyRow(excelRow, index, [6, 7]);
    excelRow.getCell(8).alignment = { horizontal: 'center', vertical: 'middle' };
    excelRow.getCell(9).alignment = { horizontal: 'right', vertical: 'middle' };
    excelRow.getCell(10).alignment = { horizontal: 'right', vertical: 'middle' };
  });

  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = Math.max(firstDataRow, headerRowIndex + rows.length);
  worksheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: 10 }
  };

  const totalRow = worksheet.getRow(lastDataRow + 1);
  totalRow.getCell(1).value = 'TOTAL GENERAL';
  totalRow.getCell(10).value = totalImporte;
  totalRow.getCell(10).numFmt = '#,##0.00';
  totalRow.height = 22;
  totalRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FF123247' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2F8' } };
    setThinBorder(cell, 'FFB9C8D6');
  });

  return worksheet;
}

function createDataSheet(workbook, rows) {
  const worksheet = workbook.addWorksheet('Datos Facturacion', {
    views: [{ state: 'frozen', ySplit: 1 }]
  });

  const tableRows = rows.map((row) => [
    row.item,
    row.id,
    row.documento,
    row.serie,
    row.numero,
    row.cliente,
    row.clienteDocumento,
    row.fechaEmisionText,
    row.fechaVencimientoText,
    row.estadoComercial,
    row.estadoCpe,
    row.estadoAnulacion,
    row.estadoGeneral,
    row.moneda,
    row.tipoCambio,
    row.subtotal,
    row.igv,
    row.total,
    row.totalPen,
    row.observaciones
  ]);

  worksheet.addTable({
    name: 'tblFacturacion',
    ref: 'A1',
    headerRow: true,
    totalsRow: true,
    style: {
      theme: 'TableStyleMedium2',
      showRowStripes: true
    },
    columns: [
      { name: 'Item', totalsRowLabel: 'Totales' },
      { name: 'ID' },
      { name: 'Documento' },
      { name: 'Serie' },
      { name: 'Numero' },
      { name: 'Cliente' },
      { name: 'DocumentoCliente' },
      { name: 'FechaEmision' },
      { name: 'FechaVencimiento' },
      { name: 'EstadoComercial' },
      { name: 'EstadoCPE' },
      { name: 'EstadoAnulacion' },
      { name: 'EstadoGeneral' },
      { name: 'Moneda' },
      { name: 'TipoCambio' },
      { name: 'Subtotal', totalsRowFunction: 'sum' },
      { name: 'IGV', totalsRowFunction: 'sum' },
      { name: 'Total', totalsRowFunction: 'sum' },
      { name: 'TotalPEN', totalsRowFunction: 'sum' },
      { name: 'Observaciones' }
    ],
    rows: tableRows
  });

  worksheet.columns = [
    { width: 8 },
    { width: 8 },
    { width: 18 },
    { width: 10 },
    { width: 10 },
    { width: 30 },
    { width: 18 },
    { width: 14 },
    { width: 14 },
    { width: 16 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 10 },
    { width: 12 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 16 },
    { width: 26 }
  ];

  worksheet.getRow(1).font = { bold: true };
  for (let rowIndex = 2; rowIndex <= rows.length + 2; rowIndex += 1) {
    worksheet.getCell(`O${rowIndex}`).numFmt = '#,##0.0000';
    worksheet.getCell(`P${rowIndex}`).numFmt = '#,##0.00';
    worksheet.getCell(`Q${rowIndex}`).numFmt = '#,##0.00';
    worksheet.getCell(`R${rowIndex}`).numFmt = '#,##0.00';
    worksheet.getCell(`S${rowIndex}`).numFmt = '#,##0.00';
  }

  worksheet.getCell('A' + (rows.length + 4)).value = 'Base lista para filtros avanzados y tabla dinamica en Excel.';
  worksheet.getCell('A' + (rows.length + 4)).font = { italic: true, color: { argb: 'FF667085' } };
}

export async function exportFacturasWorkbook({ facturas, clientes, searchText, usuario }) {
  const ExcelJSModule = await import('exceljs');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;
  const workbook = new ExcelJS.Workbook();
  const rows = buildRows(facturas, clientes);

  workbook.creator = 'InsightSoft';
  workbook.company = 'InsightSoft';
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.calcProperties.fullCalcOnLoad = true;

  createSummarySheet(workbook, rows, searchText, usuario);
  createDataSheet(workbook, rows);

  const buffer = await workbook.xlsx.writeBuffer();
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  saveAs(new Blob([buffer], { type: EXCEL_MIME }), `Facturas-Profesional-${timestamp}.xlsx`);
}